/**
 * Workspace · Petty Cash voucher composer (S34) — consolidated single-card form.
 *
 * One form card top-to-bottom: category 8-tile grid → in-card divider →
 * amount + date 2-col → narration (single line) → optional counterparty
 * toggle → V1.x photo placeholder. RIGHT pane is the sticky preview
 * (drawer impact + posting preview + recent vouchers in category).
 *
 * Supports `?category=<slug>` deep-link from the `/petty-cash`
 * `QuickAddGrid` tiles — `validateSearch` forwards it as `initialCategory`.
 *
 * @see prototype/cash/petty-cash-expense.html — consolidated `.section.section-body`
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.4
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Camera, Check, ChevronDown, Plus } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import type { pettyCash as pettyCashContract } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  CategoryPicker,
  PETTY_CASH_CATEGORIES,
  CATEGORY_META,
  type PettyCashCategory,
} from "../../../../features/petty-cash/index.ts";
import { paiseToInr, rupeesToPaiseString } from "../../../../lib/format.ts";

interface NewVoucherSearch {
  category?: PettyCashCategory;
}

/** Shape of `cashMovements.post`'s success result we care about (subset). */
interface PostMovementResult {
  awaitingApproval: { summary: string } | null;
}

/** Per-category roll-up on `pettyCash.anchorData.thisMonth.perCategory`. */
interface PerCategoryStat {
  category: PettyCashCategory;
  totalPaise: string;
  voucherCount: number;
}

type VendorType = "walk-in" | "recurring" | "employee" | "other";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/new")({
  component: NewVoucherPage,
  validateSearch: (s: Record<string, unknown>): NewVoucherSearch => {
    const out: NewVoucherSearch = {};
    if (
      typeof s.category === "string" &&
      (PETTY_CASH_CATEGORIES as readonly string[]).includes(s.category)
    ) {
      out.category = s.category as PettyCashCategory;
    }
    return out;
  },
});

function NewVoucherPage() {
  const search = Route.useSearch();
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});

  if (anchorQ.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "New voucher" },
        ]}
      >
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading composer…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }
  if (anchorQ.error || !anchorQ.data) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "New voucher" },
        ]}
      >
        <Alert
          intent="bad"
          title="Couldn't load drawer"
          description={anchorQ.error?.message ?? "Drawer not found"}
        />
      </WorkspaceShell>
    );
  }

  const initial: PettyCashCategory | undefined = search.category;

  return <NewVoucherForm anchor={anchorQ.data} initialCategory={initial} />;
}

type AnchorData = pettyCashContract.AnchorDataOutput;

function NewVoucherForm({
  anchor,
  initialCategory,
}: {
  anchor: AnchorData;
  initialCategory: PettyCashCategory | undefined;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Default to refreshments when no deep-link slug is provided — the
  // prototype ships with it pre-selected (the most-frequent category for
  // an SME drawer).
  const [category, setCategory] = useState<PettyCashCategory | undefined>(
    initialCategory ?? "refreshments",
  );
  const [amountRupees, setAmountRupees] = useState("");
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState("");
  const [counterpartyOpen, setCounterpartyOpen] = useState(false);
  const [counterparty, setCounterparty] = useState("");
  const [vendorType, setVendorType] = useState<VendorType>("walk-in");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const mutation = trpc.cashMovements.post.useMutation({
    onSuccess: async (res: PostMovementResult) => {
      // Refresh the surfaces this post mutates (balances, activity, briefings).
      await Promise.all([
        utils.pettyCash.anchorData.invalidate(),
        utils.cashMovements.list.invalidate(),
        utils.briefings.workspaceSignals.invalidate(),
        utils.briefings.today.invalidate(),
      ]);
      if (res.awaitingApproval) {
        setInfo(
          `Queued for founder review (${res.awaitingApproval.summary}). Tracked in the approvals inbox.`,
        );
      } else {
        void navigate({ to: "/cash/petty-cash" });
      }
    },
    onError: (err: { message: string }) => setError(err.message),
  });

  const drawer = anchor.drawer;
  const balancePaise = BigInt(drawer.balancePaise);
  const approvalThresholdPaise = BigInt(anchor.approvalThresholdPaise);

  // The CategoryPicker needs an 8-row tiles array with totalPaise per category.
  // We synthesise a full 8-row set from `thisMonth.perCategory`.
  const categoriesForPicker = useMemo(() => {
    const per = anchor.thisMonth.perCategory as ReadonlyArray<PerCategoryStat>;
    const map = new Map<PettyCashCategory, string>(per.map((c) => [c.category, c.totalPaise]));
    return PETTY_CASH_CATEGORIES.map((slug) => ({
      slug,
      totalPaise: map.get(slug) ?? "0",
    }));
  }, [anchor.thisMonth.perCategory]);

  const amountPaiseStr = rupeesToPaiseString(amountRupees);
  const amountPaise = amountPaiseStr ? BigInt(amountPaiseStr) : 0n;
  const afterBalance = balancePaise - amountPaise;
  const overDrawer = amountPaise > 0n && amountPaise > balancePaise;
  const overThreshold = amountPaise > 0n && amountPaise > approvalThresholdPaise;

  function resetForm() {
    setAmountRupees("");
    setNarration("");
    setCounterparty("");
    setVendorType("walk-in");
    setCounterpartyOpen(false);
  }

  function submit(e: React.SyntheticEvent, mode: "one" | "another") {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!category) {
      setError("Pick a category for this voucher.");
      return;
    }
    if (amountPaise <= 0n) {
      setError("Enter an amount greater than zero.");
      return;
    }
    if (narration.trim().length === 0) {
      setError("Add a one-line narration — the auditor will read it months from now.");
      return;
    }
    const payload: Parameters<typeof mutation.mutate>[0] = {
      kind: "petty_cash_expense",
      fromAccountId: drawer.id,
      amountPaise: amountPaise.toString(),
      valueDate,
      narration: narration.trim(),
      expenseCategory: category,
    };
    if (counterparty.trim()) payload.counterpartyLabel = counterparty.trim();

    mutation.mutate(payload, {
      onSuccess: (res: PostMovementResult) => {
        if (res.awaitingApproval) return; // handled in onSuccess above
        if (mode === "another") {
          resetForm();
          setError(null);
          setInfo("Voucher posted. Adding another…");
          return;
        }
        void navigate({ to: "/cash/petty-cash" });
      },
    });
  }

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Petty cash", to: "/cash/petty-cash" },
        { label: "New voucher" },
      ]}
    >
      <div className="flex flex-col gap-5">
        {/* Composer head — sticky on scroll */}
        <header className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/cash/petty-cash"
              className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline"
              aria-label="Back to petty cash"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight flex items-center gap-2 flex-wrap">
                New voucher
                <span className="inline-flex items-center px-1.5 py-px rounded-sm bg-sky-50 text-sky-700 border border-sky-100 text-xs font-bold uppercase tracking-wider">
                  Petty cash
                </span>
              </h1>
              <p className="mt-1 text-xs text-muted">
                {drawer.name} · {paiseToInr(drawer.balancePaise)} available · Imprest{" "}
                {paiseToInr(drawer.imprestPaise)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button asChild intent="ghost" size="md" className="border border-line">
              <Link to="/cash/petty-cash">Cancel</Link>
            </Button>
            <Button
              type="button"
              intent="ghost"
              size="md"
              className="border border-line"
              onClick={(e) => submit(e, "another")}
              disabled={mutation.isPending}
            >
              <Plus size={13} strokeWidth={2.4} aria-hidden />
              Post &amp; add another
            </Button>
            <Button
              type="button"
              intent="primary"
              size="md"
              onClick={(e) => submit(e, "one")}
              disabled={mutation.isPending}
            >
              <Check size={13} strokeWidth={2.4} aria-hidden />
              {mutation.isPending ? "Posting…" : "Post voucher"}
            </Button>
          </div>
        </header>

        {/* 2-col body — 60/40 split: form left, sticky preview right */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5">
          {/* LEFT — consolidated single-card form */}
          <form onSubmit={(e) => submit(e, "one")} className="min-w-0">
            <Card>
              <CardBody className="flex flex-col gap-2.5">
                {/* Top cue — replaces per-step heads */}
                <p className="text-xs text-muted leading-relaxed">
                  Tap a category. Then enter what it cost. One voucher = one line in the Petty Cash
                  Book.
                </p>

                {/* Category 8-tile grid */}
                <CategoryPicker
                  categories={categoriesForPicker}
                  value={category}
                  onChange={setCategory}
                />

                {/* In-card divider between category choice and entry details */}
                <hr className="border-t border-line mt-3 mb-1" aria-hidden="true" />

                {/* Amount + Date in 2-col grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-ink-2">
                      Amount <span className="text-rose-600">*</span>
                    </span>
                    <div className="flex items-center bg-paper border border-line rounded-sm focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100 overflow-hidden">
                      <span aria-hidden className="px-2 text-muted text-sm">
                        ₹
                      </span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={amountRupees}
                        onChange={(e) => setAmountRupees(e.target.value)}
                        placeholder="0"
                        className="border-0 focus-visible:ring-0 focus-visible:border-transparent"
                        aria-label="Amount in rupees"
                      />
                    </div>
                    <AmountHelper
                      amountPaise={amountPaise}
                      overDrawer={overDrawer}
                      overThreshold={overThreshold}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-ink-2">
                      Date <span className="text-rose-600">*</span>
                    </span>
                    <Input
                      type="date"
                      value={valueDate}
                      onChange={(e) => setValueDate(e.target.value)}
                      aria-label="Value date"
                    />
                  </label>
                </div>

                {/* Narration — single-line input */}
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">
                    Narration <span className="text-rose-600">*</span>
                  </span>
                  <Input
                    type="text"
                    value={narration}
                    onChange={(e) => setNarration(e.target.value)}
                    placeholder="e.g. Auto fare — site visit to Andheri vendor"
                    aria-label="Narration"
                  />
                  <span className="text-xs text-muted">
                    Short and specific. The auditor reads this 8 months from now.
                  </span>
                </label>

                {/* Counterparty (optional, collapsible) */}
                <CounterpartyFields
                  open={counterpartyOpen}
                  onToggle={() => setCounterpartyOpen((v) => !v)}
                  name={counterparty}
                  onNameChange={setCounterparty}
                  vendorType={vendorType}
                  onVendorTypeChange={setVendorType}
                />

                {/* V1.x voucher photo placeholder */}
                <PhotoPlaceholder />

                {error ? (
                  <Alert intent="bad" title="Couldn't post the voucher" description={error} />
                ) : null}
                {info ? <Alert intent="info" title="Heads up" description={info} /> : null}
              </CardBody>
            </Card>
          </form>

          {/* RIGHT — sticky preview */}
          <aside className="lg:sticky lg:top-4 self-start flex flex-col gap-4">
            <PreviewPane
              category={category}
              drawerName={drawer.name}
              beforePaise={balancePaise}
              afterPaise={afterBalance}
              amountPaise={amountPaise}
            />
            <RecentInCategoryCard
              category={category}
              perCategory={anchor.thisMonth.perCategory as ReadonlyArray<PerCategoryStat>}
            />
            <Card>
              <CardBody>
                <p className="text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded-sm p-2.5 leading-relaxed">
                  Tip: vouchers ≤ {paiseToInr(approvalThresholdPaise)} post straight; over the
                  threshold queue for founder review (ADR-0012 §10.4).
                </p>
              </CardBody>
            </Card>
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────

/**
 * Live helper text beneath the amount input.
 *
 * Three states (red > amber > green); only one renders at a time:
 *   - red    → amount exceeds the drawer's available balance (blocker).
 *   - amber  → amount exceeds the founder-review threshold (will queue).
 *   - green  → amount is under the threshold (will post straight).
 */
function AmountHelper({
  amountPaise,
  overDrawer,
  overThreshold,
}: {
  amountPaise: bigint;
  overDrawer: boolean;
  overThreshold: boolean;
}) {
  if (amountPaise <= 0n) return null;
  if (overDrawer) {
    return (
      <span className="text-xs text-bad font-medium">
        Exceeds drawer balance — replenish first.
      </span>
    );
  }
  if (overThreshold) {
    return (
      <span className="text-xs text-warn font-medium">
        Will queue for founder review (ADR-0012 §10.4).
      </span>
    );
  }
  return (
    <span className="text-xs text-ok font-medium">
      Under the founder-review threshold (ADR-0012 §10.4).
    </span>
  );
}

function CounterpartyFields({
  open,
  onToggle,
  name,
  onNameChange,
  vendorType,
  onVendorTypeChange,
}: {
  open: boolean;
  onToggle: () => void;
  name: string;
  onNameChange: (v: string) => void;
  vendorType: VendorType;
  onVendorTypeChange: (v: VendorType) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="cp-fields"
        className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-dashed border-line-2 bg-paper text-muted hover:text-ink-2 hover:bg-bg hover:border-line text-xs font-semibold transition-colors"
      >
        {open ? (
          <ChevronDown size={13} strokeWidth={2.4} aria-hidden />
        ) : (
          <Plus size={13} strokeWidth={2.4} aria-hidden />
        )}
        {open ? "Hide counterparty" : "Add counterparty (optional)"}
      </button>
      {open ? (
        <div id="cp-fields" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">
              Counterparty name <span className="text-muted font-medium text-xs">(free text)</span>
            </span>
            <Input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Sharma Chai Stall, Speedway Courier"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">Vendor type</span>
            <Select value={vendorType} onValueChange={(v) => onVendorTypeChange(v as VendorType)}>
              <SelectTrigger aria-label="Vendor type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="walk-in">Walk-in / informal</SelectItem>
                <SelectItem value="recurring">
                  Recurring (likely worth onboarding as vendor)
                </SelectItem>
                <SelectItem value="employee">Employee reimbursement</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
      ) : null}
    </div>
  );
}

function PhotoPlaceholder() {
  return (
    <div className="relative border border-dashed border-line-2 rounded-md bg-raised p-3.5 mt-1">
      <span className="absolute top-3 right-3.5 px-2 py-0.5 rounded-full bg-paper border border-line text-xs font-semibold uppercase tracking-wider text-muted">
        V1.x · coming soon
      </span>
      <h4 className="flex items-center gap-2 text-xs font-bold text-ink-2 mb-1">
        <span className="inline-grid place-items-center w-[22px] h-[22px] rounded-md bg-paper border border-line text-muted">
          <Camera size={12} strokeWidth={2} aria-hidden />
        </span>
        Attach voucher photo
      </h4>
      <p className="text-xs text-muted leading-relaxed pr-24">
        Snap a photo of the chai-wala's chit for the auditor trail. Lands when the OCR extractors
        ship — until then, keep the physical voucher in the drawer.
      </p>
    </div>
  );
}

function PreviewPane({
  category,
  drawerName,
  beforePaise,
  afterPaise,
  amountPaise,
}: {
  category: PettyCashCategory | undefined;
  drawerName: string;
  beforePaise: bigint;
  afterPaise: bigint;
  amountPaise: bigint;
}) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1.5">
          Drawer impact
        </p>
        <p className="text-sm text-ink-2 font-semibold mb-2">{drawerName}</p>
        <div className="flex items-baseline gap-2 num tabular-nums">
          <span className="text-muted text-sm">{paiseToInr(beforePaise)}</span>
          <span className="text-muted">→</span>
          <span
            className={
              afterPaise < 0n ? "text-bad text-base font-bold" : "text-ink text-base font-bold"
            }
          >
            {paiseToInr(afterPaise)}
          </span>
        </div>
        {amountPaise > 0n ? (
          <p className="text-xs text-muted mt-2">
            Posts as{" "}
            <strong className="font-semibold text-ink-2">−{paiseToInr(amountPaise)}</strong> against{" "}
            {category ? CATEGORY_META[category].label : "—"}.
          </p>
        ) : (
          <p className="text-xs text-muted mt-2">Enter an amount to see the projection.</p>
        )}
        {afterPaise < 0n ? (
          <p className="mt-3 text-xs text-bad bg-bad-50 border border-bad-100 rounded-sm p-2">
            Drawer would go negative — replenish the float first.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RecentInCategoryCard({
  category,
  perCategory,
}: {
  category: PettyCashCategory | undefined;
  perCategory: ReadonlyArray<{
    category: PettyCashCategory;
    totalPaise: string;
    voucherCount: number;
  }>;
}) {
  if (!category) {
    return (
      <Card>
        <CardBody>
          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1">
            Category trend
          </p>
          <p className="text-sm text-muted">Pick a category to see this month's spend.</p>
        </CardBody>
      </Card>
    );
  }
  const stat = perCategory.find((c) => c.category === category);
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1">
          {CATEGORY_META[category].label} · this month
        </p>
        <p className="text-2xl font-bold tracking-tight num">
          {paiseToInr(stat?.totalPaise ?? "0")}
        </p>
        <p className="text-xs text-muted mt-1">
          {stat?.voucherCount ?? 0} {stat?.voucherCount === 1 ? "voucher" : "vouchers"} posted so
          far this month.
        </p>
      </CardBody>
    </Card>
  );
}
